import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { queryClient, trpc } from "../../app/trpc.js";
import { Badge, Button, Card, Chip, Input, Label, MenuRow, Notice, PageTitle, Sheet, Spinner, Toggle } from "../../ui/index.js";
import { fmtMoney, useCabinet } from "../cabinet/index.js";

interface Draft {
  id?: string;
  name: string;
  price: string;
  durationMin: string;
  bufferMin: string;
  active: boolean;
  /** Мастера, которые делают услугу (без мастера услугу никто не запишет). */
  masterIds: string[];
}
const digits = (s: string) => s.replace(/\D/g, "");

/** Услуги салона: название, цена в RSD, длительность, буфер после; выключенную бот не предлагает. Только владелец. */
export function ServicesPage() {
  const { t, i18n } = useTranslation();
  const cab = useCabinet();
  const list = useQuery(trpc.calendar.services.list.queryOptions({ salonId: cab.salonId, includeInactive: true }));
  const masters = useQuery(trpc.calendar.masters.list.queryOptions({ salonId: cab.salonId, includeInactive: true }));
  const [draft, setDraft] = useState<Draft | null>(null);
  const saveService = useMutation(trpc.calendar.services.save.mutationOptions());
  const saveMaster = useMutation(trpc.calendar.masters.save.mutationOptions());
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);
  const empty = (): Draft => ({
    name: "",
    price: "",
    durationMin: "30",
    bufferMin: "0",
    active: true,
    masterIds: masters.data?.filter((m) => m.active).map((m) => m.id) ?? [],
  });

  /** Услуга, затем мастера, у которых изменился состав услуг. */
  const submit = async (d: Draft) => {
    setSaving(true);
    setFailed(false);
    try {
      const svc = await saveService.mutateAsync({
        ...(d.id ? { id: d.id } : {}),
        salonId: cab.salonId,
        name: d.name.trim(),
        price: { amount: Number(d.price), currency: "RSD" },
        durationMin: Number(d.durationMin),
        bufferMin: Number(d.bufferMin || 0),
        active: d.active,
      });
      for (const m of masters.data ?? []) {
        const has = m.serviceIds.includes(svc.id);
        const want = d.masterIds.includes(m.id);
        if (has === want) continue;
        const serviceIds = want ? [...m.serviceIds, svc.id] : m.serviceIds.filter((x) => x !== svc.id);
        await saveMaster.mutateAsync({ id: m.id, salonId: cab.salonId, name: m.name, active: m.active, serviceIds });
      }
      void queryClient.invalidateQueries(trpc.calendar.pathFilter());
      setDraft(null);
    } catch {
      setFailed(true);
    } finally {
      setSaving(false);
    }
  };
  const valid = draft?.name.trim() && draft.price !== "" && Number(draft.durationMin) >= 5;

  return (
    <div className="lg:max-w-3xl">
      <PageTitle>{t("services.title")}</PageTitle>
      {list.isPending && <Spinner />}
      {list.data?.length === 0 && <Notice>{t("services.empty")}</Notice>}
      {list.data && list.data.length > 0 && (
        <Card className="py-1">
          {list.data.map((s) => (
            <MenuRow
              key={s.id}
              title={s.name}
              hint={`${fmtMoney(s.price.amount, i18n.language)} · ${s.durationMin}′${s.bufferMin ? ` + ${s.bufferMin}′` : ""}`}
              right={s.active ? undefined : <Badge>{t("services.off")}</Badge>}
              onClick={() =>
                setDraft({
                  id: s.id,
                  name: s.name,
                  price: String(s.price.amount),
                  durationMin: String(s.durationMin),
                  bufferMin: String(s.bufferMin),
                  active: s.active,
                  masterIds: masters.data?.filter((m) => m.serviceIds.includes(s.id)).map((m) => m.id) ?? [],
                })
              }
            />
          ))}
        </Card>
      )}
      <Button className="mt-4 w-full lg:w-auto" onClick={() => setDraft(empty())}>
        {t("services.add")}
      </Button>

      <Sheet open={draft !== null} onClose={() => setDraft(null)} title={draft?.id ? t("services.edit") : t("services.add")}>
        {draft && (
          <div className="space-y-3">
            <div>
              <Label htmlFor="sv-name">{t("services.name")}</Label>
              <Input id="sv-name" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
            </div>
            <div className="flex gap-2">
              <div className="flex-1">
                <Label htmlFor="sv-price">{t("services.price")}</Label>
                <Input id="sv-price" inputMode="numeric" value={draft.price} onChange={(e) => setDraft({ ...draft, price: digits(e.target.value) })} />
              </div>
              <div className="flex-1">
                <Label htmlFor="sv-dur">{t("services.duration")}</Label>
                <Input
                  id="sv-dur"
                  inputMode="numeric"
                  value={draft.durationMin}
                  onChange={(e) => setDraft({ ...draft, durationMin: digits(e.target.value) })}
                />
              </div>
            </div>
            <div>
              <Label htmlFor="sv-buf">{t("services.buffer")}</Label>
              <Input id="sv-buf" inputMode="numeric" value={draft.bufferMin} onChange={(e) => setDraft({ ...draft, bufferMin: digits(e.target.value) })} />
            </div>
            <Toggle checked={draft.active} onChange={(v) => setDraft({ ...draft, active: v })} label={t("services.active")} />
            <div>
              <Label>{t("services.masters")}</Label>
              <div className="flex flex-wrap gap-2">
                {masters.data?.map((m) => {
                  const on = draft.masterIds.includes(m.id);
                  return (
                    <Chip
                      key={m.id}
                      active={on}
                      onClick={() => setDraft({ ...draft, masterIds: on ? draft.masterIds.filter((x) => x !== m.id) : [...draft.masterIds, m.id] })}
                    >
                      <span className="inline-flex items-center gap-1.5">
                        <span className={on ? "inline-block w-4" : "inline-block w-4 text-transparent"} aria-hidden="true">
                          ✓
                        </span>
                        {m.name}
                      </span>
                    </Chip>
                  );
                })}
              </div>
              {draft.masterIds.length === 0 && <p className="mt-1 text-sm text-amber-700">{t("services.noMasters")}</p>}
            </div>
            {failed && <Notice kind="error">{t("common.error")}</Notice>}
            <Button className="w-full" disabled={!valid || saving} onClick={() => void submit(draft)}>
              {t("settings.save")}
            </Button>
          </div>
        )}
      </Sheet>
    </div>
  );
}
